import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import type { OrderDetail } from '../../lib/orders';
import { formatCurrency, formatDateTime } from '../../lib/format';
import {
  cancelReparatie,
  fetchReparatieVoorbeeldUrls,
  isInterneReparatie,
  listReparaties,
  markReparatieKleedAfgeleverd,
  sendReparatieMailAgain,
  type RepairItemSummary,
  type ReparatieOpdracht,
  type RitInfo,
  type ReparatieStatus,
  type VolgendeStap,
} from '../../lib/reparatie';
import { ReparatieAanvraagDialog } from './ReparatieAanvraagDialog';
import { ReparatieInkoopDialog } from './ReparatieInkoopDialog';
import { ReparatieAkkoordDialog } from './ReparatieAkkoordDialog';
import { ReparatiePrijsDialog } from './ReparatiePrijsDialog';
import { ReparatieOffertePreviewDialog } from './ReparatieOffertePreviewDialog';
import { ReparatieCorrectieDialog, type CorrectieSoort } from './ReparatieCorrectieDialog';
import { ReparatieKleedTijdlijn } from './ReparatieKleedTijdlijn';
import { PlanRitDialog } from './PlanRitDialog';
import { BerichtThread } from '../berichten/BerichtThread';

/*
 * Visuele grammatica van deze kaart (bewust een regel, geen smaak):
 *  - status  = outline-pil (`pil`), zelfstandig naamwoord, geen werkwoord, geen ✓
 *  - actie   = solide knop met werkwoord in gebiedende wijs; één primaire
 *              (indigo) knop per kaart, de rest secundair (slate)
 *  - correctie = tekstknop in het ⋯-menu, altijd met dialoog en reden
 * Casus REP-0002510 (03-09-2026): de groene "Klaar bij reparateur ✓" was een
 * knop die als status las, en "Alles tegelijk: gebracht" stond zonder toets
 * op een kleed dat nog bij de klant lag.
 */

const STATUS_PIL: Record<ReparatieStatus, string> = {
  'Aanvraag verstuurd': 'border-blue-300 text-blue-700',
  'Offerte ontvangen': 'border-amber-300 text-amber-700',
  'Offerte naar klant': 'border-purple-300 text-purple-700',
  'Klant akkoord': 'border-emerald-300 text-emerald-700',
  'Bij reparateur': 'border-indigo-300 text-indigo-700',
  'Terug in wasserij': 'border-teal-300 text-teal-700',
  'Afgerond': 'border-slate-300 text-slate-600',
  'Geannuleerd': 'border-rose-300 text-rose-700',
  'Geweigerd': 'border-rose-300 text-rose-700',
};

const STATUS_LABEL: Partial<Record<ReparatieStatus, string>> = {
  'Aanvraag verstuurd': 'Wacht op prijs reparateur',
  'Offerte ontvangen': 'Prijs binnen',
  'Offerte naar klant': 'Wacht op klant',
  'Klant akkoord': 'Akkoord',
};

/** Statussen waarin de kleedfase het verhaal al vertelt; de opdrachtpil is dan ruis. */
const STATUS_PIL_VERBORGEN: ReparatieStatus[] = ['Bij reparateur', 'Terug in wasserij'];

const FASE_PIL: Record<string, string> = {
  bij_klant: 'border-slate-300 text-slate-600',
  in_wasserij: 'border-slate-300 text-slate-600',
  heen_gepland: 'border-blue-300 text-blue-700',
  bij_reparateur: 'border-indigo-300 text-indigo-700',
  klaar: 'border-emerald-300 text-emerald-700',
  terug_gepland: 'border-blue-300 text-blue-700',
  terug_in_wasserij: 'border-teal-300 text-teal-700',
};

const pil = 'inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border bg-white font-medium';
const knopPrimair =
  'px-2.5 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed';
const knopSecundair =
  'px-2.5 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed';
const knopTekst = 'px-2 py-1 rounded text-slate-500 hover:text-slate-800 hover:bg-slate-100';

interface Props {
  order: OrderDetail;
}

interface PlanDialogState {
  opdracht: ReparatieOpdracht;
  mode: 'heen' | 'terug';
  wiId?: string;
  wiIds?: string[];
  wiLabel?: string | null;
  wiLabels?: string[];
  currentRitId?: string | null;
}

interface CorrectieState {
  soort: CorrectieSoort;
  opdracht: ReparatieOpdracht;
  item: RepairItemSummary;
}

export function ReparatieSection({ order }: Props) {
  const [opdrachten, setOpdrachten] = useState<ReparatieOpdracht[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aanvraagOpen, setAanvraagOpen] = useState(false);
  const [aanvraagPrefillWiId, setAanvraagPrefillWiId] = useState<string | null>(null);
  const [inkoopFor, setInkoopFor] = useState<ReparatieOpdracht | null>(null);
  const [akkoordFor, setAkkoordFor] = useState<ReparatieOpdracht | null>(null);
  const [cancelFor, setCancelFor] = useState<ReparatieOpdracht | null>(null);
  const [prijsFor, setPrijsFor] = useState<ReparatieOpdracht | null>(null);
  const [offerteFor, setOfferteFor] = useState<ReparatieOpdracht | null>(null);
  const [correctie, setCorrectie] = useState<CorrectieState | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [planDialog, setPlanDialog] = useState<PlanDialogState | null>(null);
  // Ritten die we net zelf geboekt hebben maar die de mirror nog niet kent.
  // Sleutel: `${wiId}:${mode}`. Bewust een ref en geen state: hij wordt alleen
  // gelezen tijdens refresh(), dus state zou refresh() opnieuw maken en de
  // laad-useEffect in een lus trekken.
  const verseRittenRef = useRef<Record<string, RitInfo>>({});
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open via deeplink uit /reparaties?tab=teplannen "Maak repair-order →".
  useEffect(() => {
    const flag = searchParams.get('openReparatie');
    const wiId = searchParams.get('wiId');
    if (flag !== '1') return;
    setAanvraagPrefillWiId(wiId || null);
    setAanvraagOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('openReparatie');
    next.delete('wiId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listReparaties(order.id);
      for (const o of list) {
        for (const it of o.items) {
          if (it.geplandeRitHeen) delete verseRittenRef.current[`${it.wiId}:heen`];
          if (it.geplandeRitTerug) delete verseRittenRef.current[`${it.wiId}:terug`];
        }
      }
      setOpdrachten(applyVerseRitten(list, verseRittenRef.current));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [order.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAction = async (
    key: string,
    action: () => Promise<{ success: boolean; message?: string }>,
    label: string
  ) => {
    setBusyKey(key);
    setError(null);
    try {
      const res = await action();
      if (!res.success) {
        setError(`${label}: ${res.message || 'mislukt'}`);
      } else {
        await refresh();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `${label} mislukt`);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <>
      <div className="bg-slate-50/40 rounded-xl border border-slate-200 p-4">
        <div className={clsx('flex items-center justify-between', opdrachten.length > 0 && 'mb-3')}>
          <h2 className="text-sm font-semibold text-slate-900">
            Reparaties{!loading && ` (${opdrachten.length})`}
          </h2>
          <button
            type="button"
            onClick={() => setAanvraagOpen(true)}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
          >
            + Reparatie aanvragen
          </button>
        </div>

        {loading && <p className="text-sm text-slate-500 mt-2">Laden…</p>}
        {error && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5 mt-2 mb-2">
            {error}
          </p>
        )}

        {opdrachten.length > 0 && (
          <div className="space-y-2">
            {opdrachten.map((o) => (
              <OpdrachtCard
                key={o.id}
                opdracht={o}
                busyKey={busyKey}
                onAction={handleAction}
                onOpenInkoop={() => setInkoopFor(o)}
                onOpenAkkoord={() => setAkkoordFor(o)}
                onOpenCancel={() => setCancelFor(o)}
                onOpenPrijs={() => setPrijsFor(o)}
                onOpenOfferte={() => setOfferteFor(o)}
                onOpenPlan={(state) => setPlanDialog(state)}
                onOpenCorrectie={(soort, item) => setCorrectie({ soort, opdracht: o, item })}
              />
            ))}
          </div>
        )}
      </div>

      {aanvraagOpen && (
        <ReparatieAanvraagDialog
          order={order}
          prefillSelectedWiId={aanvraagPrefillWiId}
          onClose={() => {
            setAanvraagOpen(false);
            setAanvraagPrefillWiId(null);
          }}
          onCreated={() => {
            setAanvraagOpen(false);
            setAanvraagPrefillWiId(null);
            refresh();
          }}
        />
      )}
      {inkoopFor && (
        <ReparatieInkoopDialog
          opdracht={inkoopFor}
          onClose={() => setInkoopFor(null)}
          onUpdated={() => {
            setInkoopFor(null);
            refresh();
          }}
        />
      )}
      {akkoordFor && (
        <ReparatieAkkoordDialog
          opdracht={akkoordFor}
          onClose={() => setAkkoordFor(null)}
          onDone={() => {
            setAkkoordFor(null);
            refresh();
          }}
        />
      )}
      {cancelFor && (
        <ReparatieAnnulerenDialog
          opdracht={cancelFor}
          onClose={() => setCancelFor(null)}
          onCancelled={() => {
            setCancelFor(null);
            refresh();
          }}
        />
      )}
      {offerteFor && (
        <ReparatieOffertePreviewDialog
          opdracht={offerteFor}
          onClose={() => setOfferteFor(null)}
          onSent={() => {
            setOfferteFor(null);
            refresh();
          }}
        />
      )}
      {prijsFor && (
        <ReparatiePrijsDialog
          opdracht={prijsFor}
          onClose={() => setPrijsFor(null)}
          onDone={() => {
            setPrijsFor(null);
            refresh();
          }}
        />
      )}
      {correctie && (
        <ReparatieCorrectieDialog
          soort={correctie.soort}
          opdracht={correctie.opdracht}
          item={correctie.item}
          onClose={() => setCorrectie(null)}
          onDone={() => {
            setCorrectie(null);
            refresh();
          }}
        />
      )}
      {planDialog && planDialog.opdracht.reparateurId && (
        <PlanRitDialog
          reparateurId={planDialog.opdracht.reparateurId}
          reparateurNaam={planDialog.opdracht.reparateurNaam}
          mode={planDialog.mode}
          wiId={planDialog.wiId}
          wiIds={planDialog.wiIds}
          wiLabel={planDialog.wiLabel}
          wiLabels={planDialog.wiLabels}
          currentRitId={planDialog.currentRitId}
          onClose={() => setPlanDialog(null)}
          onPlanned={(vers) => {
            setPlanDialog(null);
            if (vers?.ritId) {
              for (const wiId of vers.wiIds) {
                verseRittenRef.current[`${wiId}:${vers.mode}`] = {
                  id: vers.ritId,
                  datum: vers.datum,
                  routeNaam: null,
                  status: 'Ingepland',
                };
              }
            }
            refresh();
          }}
        />
      )}
    </>
  );
}

// =====================================================================

type ActionFn = (
  key: string,
  action: () => Promise<{ success: boolean; message?: string }>,
  label: string
) => Promise<void>;

interface OpdrachtCardProps {
  opdracht: ReparatieOpdracht;
  busyKey: string | null;
  onAction: ActionFn;
  onOpenInkoop: () => void;
  onOpenAkkoord: () => void;
  onOpenCancel: () => void;
  onOpenPrijs: () => void;
  onOpenOfferte: () => void;
  onOpenPlan: (state: PlanDialogState) => void;
  onOpenCorrectie: (soort: CorrectieSoort, item: RepairItemSummary) => void;
}

// Annuleren kan zolang de klant nog niet akkoord is — daarna handmatig (server bewaakt dit ook).
const CANCELLABLE_STATUSES: ReparatieStatus[] = [
  'Aanvraag verstuurd',
  'Offerte ontvangen',
  'Offerte naar klant',
];

// Dood: hier valt niets meer te plannen of te markeren.
const TERMINALE_STATUSSEN: ReparatieStatus[] = ['Afgerond', 'Geannuleerd', 'Geweigerd'];

// Na het akkoord: prijs wijzigen loopt via wijzig-prijs (klantprijs + factuurregel),
// niet via de inkoopdialoog (die zet de status terug op 'Offerte ontvangen').
const NA_AKKOORD_STATUSSEN: ReparatieStatus[] = ['Klant akkoord', 'Bij reparateur', 'Terug in wasserij'];

// Vanaf hier is besloten dát het kleed naar de reparateur gaat, dus mag de
// heen-rit gepland worden. Bij een interne opdracht direct.
const PLANBARE_STATUSSEN: ReparatieStatus[] = ['Klant akkoord', 'Bij reparateur', 'Terug in wasserij'];

function OpdrachtCard({
  opdracht: o,
  busyKey,
  onAction,
  onOpenInkoop,
  onOpenAkkoord,
  onOpenCancel,
  onOpenPrijs,
  onOpenOfferte,
  onOpenPlan,
  onOpenCorrectie,
}: OpdrachtCardProps) {
  const opdrachtBusy = busyKey === o.id;
  const isOfferteOntvangen = o.status === 'Offerte ontvangen';
  const isIntern = isInterneReparatie(o);
  const isCancellable = CANCELLABLE_STATUSES.includes(o.status);
  const voorbeeldPaths = o.voorbeeldFotoPaths ?? [];
  const [voorbeeldUrls, setVoorbeeldUrls] = useState<string[]>([]);
  const vs: VolgendeStap | undefined = o.volgendeStap;

  useEffect(() => {
    if (!isOfferteOntvangen || voorbeeldPaths.length === 0) return;
    let cancelled = false;
    fetchReparatieVoorbeeldUrls(voorbeeldPaths)
      .then((urls) => {
        if (!cancelled) setVoorbeeldUrls(urls.filter((u): u is string => !!u));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o.id, isOfferteOntvangen]);

  const showItems = o.items.length > 0 && !TERMINALE_STATUSSEN.includes(o.status);
  const toonStatusPil = !STATUS_PIL_VERBORGEN.includes(o.status);

  // De primaire knop volgt de volgende stap uit de API. Eén knop, één betekenis.
  const primair = (() => {
    if (!vs || !vs.primaireKnop) return null;
    const label = opdrachtBusy ? 'Bezig…' : vs.primaireKnop;
    switch (vs.actie) {
      case 'wacht-reparateur':
      case 'inkoop':
        return { label, onClick: onOpenInkoop };
      case 'send-quote':
        // Eerst zien wat er uitgaat; de dialoog verstuurt zelf.
        return { label, onClick: onOpenOfferte };
      case 'wacht-klant':
      case 'akkoord':
        return { label, onClick: onOpenAkkoord };
      case 'plan-heen': {
        const item = o.items.find((i) => !i.geplandeRitHeen && !i.reparatieAfgeleverdOp);
        if (!item || !o.reparateurId) return null;
        return {
          label,
          onClick: () => onOpenPlan({ opdracht: o, mode: 'heen', wiId: item.wiId, wiLabel: item.wiNaam }),
        };
      }
      case 'markeer-gebracht': {
        // Interne reparateur (Havelte): geen rit, kantoor stempelt; de guard
        // laat dit door zonder force.
        const item = o.items.find((i) => !i.reparatieAfgeleverdOp);
        if (!item) return null;
        return {
          label,
          onClick: () =>
            onAction(o.id, () => markReparatieKleedAfgeleverd(item.wiId), 'Markeer gebracht'),
        };
      }
      case 'plan-terug': {
        const items = o.items.filter((i) => i.reparatieKlaarOp && !i.reparatieOpgehaaldOp && !i.geplandeRitTerug);
        if (items.length === 0 || !o.reparateurId) return null;
        return {
          label,
          onClick: () =>
            onOpenPlan({
              opdracht: o,
              mode: 'terug',
              wiIds: items.map((i) => i.wiId),
              wiLabels: items.map((i) => i.wiNaam),
            }),
        };
      }
      default:
        return null;
    }
  })();

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-2.5">
      {/* Kop: wie, wat, hoeveel — geen knoppen */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-900">{o.opdrachtNummer}</span>
          {toonStatusPil && (
            <span className={clsx(pil, STATUS_PIL[o.status] ?? 'border-slate-300 text-slate-600')}>
              {STATUS_LABEL[o.status] ?? o.status}
            </span>
          )}
          {isIntern && (
            <span
              className={clsx(pil, 'border-violet-300 text-violet-700')}
              title="Eigen schade of coulance. Geen offerte, geen factuurregel, klant betaalt niets."
            >
              Intern — wij betalen
            </span>
          )}
          {o.reparateurNaam && <span className="text-xs text-slate-500">{o.reparateurNaam}</span>}
          <span className="text-xs text-slate-400">
            {o.aantalKleden} {o.aantalKleden === 1 ? 'kleed' : 'kleden'}
          </span>
        </div>
        {o.verkoopBedrag != null && (
          <span className="text-sm font-medium text-slate-900">{formatCurrency(o.verkoopBedrag)}</span>
        )}
      </div>

      {/* Prijs van de reparateur, zolang die nog beoordeeld moet worden */}
      {isOfferteOntvangen && (o.doorlooptijdWerkdagen != null || o.voorbeeldToelichting || voorbeeldUrls.length > 0) && (
        <div className="rounded-md bg-amber-50/60 border border-amber-100 px-2.5 py-2 space-y-1.5">
          {o.inkoopBedrag != null && (
            <p className="text-xs text-slate-600">
              Inkoop {formatCurrency(o.inkoopBedrag)}
              {o.doorlooptijdWerkdagen != null &&
                ` · ± ${o.doorlooptijdWerkdagen} werkdag${o.doorlooptijdWerkdagen === 1 ? '' : 'en'}`}
            </p>
          )}
          {o.voorbeeldToelichting && <p className="text-xs text-slate-600">{o.voorbeeldToelichting}</p>}
          {voorbeeldUrls.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {voorbeeldUrls.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noreferrer">
                  <img src={url} alt={`Voorbeeld ${i + 1}`} className="w-12 h-12 object-cover rounded border border-amber-200" />
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Volgende stap: één zin, één primaire knop */}
      {vs && (
        <div
          className={clsx(
            'rounded-md px-2.5 py-2 flex items-center justify-between gap-3 flex-wrap',
            vs.primaireKnop ? 'bg-indigo-50/70 border border-indigo-100' : 'bg-slate-50 border border-slate-100'
          )}
        >
          <p className="text-xs text-slate-700 flex-1 min-w-[12rem]">
            <span className="font-medium text-slate-900">Volgende stap: </span>
            {vs.tekst}
          </p>
          {primair && (
            <button type="button" onClick={primair.onClick} disabled={opdrachtBusy} className={clsx('text-xs', knopPrimair)}>
              {primair.label}
            </button>
          )}
        </div>
      )}

      {/* Secundaire acties: alles wat kán maar niet de bedoeling van nu is */}
      <div className="flex items-center gap-1.5 flex-wrap text-xs">
        {o.status === 'Aanvraag verstuurd' && (
          <button
            type="button"
            onClick={() => onAction(o.id, () => sendReparatieMailAgain(o.id), 'Mail opnieuw versturen')}
            disabled={opdrachtBusy}
            className={knopSecundair}
          >
            Stuur aanvraag opnieuw
          </button>
        )}
        {o.status === 'Offerte ontvangen' && !isIntern && (
          <button
            type="button"
            onClick={onOpenAkkoord}
            disabled={opdrachtBusy}
            className={knopSecundair}
            title="Klant heeft al akkoord gegeven per mail, telefoon of WhatsApp"
          >
            Leg klant-akkoord vast
          </button>
        )}
        {(o.status === 'Offerte ontvangen' || o.status === 'Offerte naar klant') && (
          <button type="button" onClick={onOpenInkoop} disabled={opdrachtBusy} className={knopSecundair}>
            Pas prijs aan
          </button>
        )}
        {/* Na het akkoord is de klantprijs het uitgangspunt en moet de
            factuurregel mee — een ander pad dan de inkoopdialoog. */}
        {NA_AKKOORD_STATUSSEN.includes(o.status) && !isIntern && (
          <button
            type="button"
            onClick={onOpenPrijs}
            disabled={opdrachtBusy}
            className={knopSecundair}
            title="Bedrag klopt niet meer (bv. per kant geprijsd). Past de opdracht, de factuurregel en de betaallink aan."
          >
            Pas prijs aan
          </button>
        )}
        {o.status === 'Offerte naar klant' && o.token && (
          <a
            href={`https://client.kleda.nl/reparatie/${o.token}`}
            target="_blank"
            rel="noreferrer"
            className={knopSecundair}
          >
            Open klant-link
          </a>
        )}
        {isCancellable && (
          <button
            type="button"
            onClick={onOpenCancel}
            disabled={opdrachtBusy}
            className={clsx(knopTekst, 'text-rose-600 hover:text-rose-700 hover:bg-rose-50')}
            title="Annuleer deze reparatie-opdracht (kan alleen vóór klant-akkoord)"
          >
            Annuleer opdracht
          </button>
        )}
      </div>

      {showItems && (
        <div className="border-t border-slate-100 pt-2 space-y-1.5">
          <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Kleden</p>
          <div className="space-y-1.5">
            {o.items.map((it) => (
              <ItemRow
                key={it.wiId}
                opdracht={o}
                item={it}
                busyKey={busyKey}
                onOpenPlan={onOpenPlan}
                onOpenCorrectie={onOpenCorrectie}
              />
            ))}
          </div>
        </div>
      )}

      {o.status === 'Geweigerd' && (
        <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
          Klant weigerde de reparatie{o.weigerReden ? `: "${o.weigerReden}"` : ''}.
        </div>
      )}
      {o.status === 'Geannuleerd' && (
        <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
          Geannuleerd door kantoor{o.annuleringReden ? `: "${o.annuleringReden}"` : ''}
          {o.geannuleerdOp ? ` (${formatDateTime(o.geannuleerdOp)})` : ''}.
        </div>
      )}
      {o.reparateurKanNiet && (
        <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
          Reparateur kan deze aanvraag niet uitvoeren
          {o.afwijzingReden ? `: "${o.afwijzingReden}"` : ''}. Annuleer of maak een nieuwe aanvraag.
        </div>
      )}

      <div className="text-[11px] text-slate-400 flex flex-wrap gap-x-3">
        {o.offerteAangevraagdOp && <span>Aangevraagd {formatDateTime(o.offerteAangevraagdOp)}</span>}
        {o.klantAkkoordOp && <span>Akkoord {formatDateTime(o.klantAkkoordOp)}</span>}
        {o.gebrachtOp && <span>Bij reparateur sinds {formatDateTime(o.gebrachtOp)}</span>}
        {o.opgehaaldOp && <span>Terug sinds {formatDateTime(o.opgehaaldOp)}</span>}
      </div>

      <BerichtThread peer="reparateur" reparatieOpdrachtId={o.id} />
    </div>
  );
}

// =====================================================================

interface ItemRowProps {
  opdracht: ReparatieOpdracht;
  item: RepairItemSummary;
  busyKey: string | null;
  onOpenPlan: (state: PlanDialogState) => void;
  onOpenCorrectie: (soort: CorrectieSoort, item: RepairItemSummary) => void;
}

function ItemRow({ opdracht, item, busyKey, onOpenPlan, onOpenCorrectie }: ItemRowProps) {
  const busy = busyKey === item.wiId || busyKey === opdracht.id;
  const fase = item.fase ?? 'bij_klant';
  // Twee redenen om de heen-rit nog niet te mogen plannen. De tweede is
  // fysiek: een rit boeken voor een kleed dat nog bij de klant ligt legt een
  // rit vóór het kleed er is (casus WB-323684). De bewaking plant hem vanzelf
  // zodra het kleed gewassen is.
  const statusToestaat = isInterneReparatie(opdracht) || PLANBARE_STATUSSEN.includes(opdracht.status);
  const kleedIsBinnen = fase !== 'bij_klant';
  const planToegestaan = statusToestaat && kleedIsBinnen;
  const planGeblokkeerdReden = !statusToestaat
    ? 'Kan pas na klant-akkoord. Eigen schade of coulance? Maak de opdracht dan aan als interne reparatie.'
    : !kleedIsBinnen
      ? 'Het kleed is nog niet opgehaald bij de klant. De heen-rit wordt vanzelf gepland zodra het gewassen is.'
      : null;
  const kanHeen = !item.reparatieAfgeleverdOp && !!opdracht.reparateurId;
  const kanTerug = !!item.reparatieKlaarOp && !item.reparatieOpgehaaldOp && !!opdracht.reparateurId;

  // Correcties die in deze fase betekenis hebben. Alles achter een dialoog.
  const correcties: Array<{ soort: CorrectieSoort; label: string }> = [];
  if (!item.reparatieAfgeleverdOp) {
    correcties.push({ soort: 'markeer-gebracht', label: 'Markeer gebracht (handmatig)' });
  } else if (!item.reparatieKlaarOp && !item.reparatieOpgehaaldOp) {
    correcties.push({ soort: 'markeer-klaar', label: 'Markeer klaar (namens reparateur)' });
    correcties.push({ soort: 'undo-gebracht', label: 'Gebracht terugdraaien' });
  } else if (item.reparatieKlaarOp && !item.reparatieOpgehaaldOp) {
    correcties.push({ soort: 'markeer-opgehaald', label: 'Markeer opgehaald (handmatig)' });
    correcties.push({ soort: 'undo-klaar', label: 'Klaar terugdraaien' });
  } else if (item.reparatieOpgehaaldOp) {
    correcties.push({ soort: 'undo-opgehaald', label: 'Opgehaald terugdraaien' });
  }

  // Eindfoto's (Fase 2) — signed bij render via dezelfde foto-url-helper als de voorbeeld-foto's.
  const eindfotoPaths = item.eindfotoPaths ?? [];
  const [eindfotoUrls, setEindfotoUrls] = useState<string[]>([]);
  useEffect(() => {
    if (eindfotoPaths.length === 0) {
      setEindfotoUrls([]);
      return;
    }
    let cancelled = false;
    fetchReparatieVoorbeeldUrls(eindfotoPaths)
      .then((urls) => {
        if (!cancelled) setEindfotoUrls(urls.filter((u): u is string => !!u));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.wiId, eindfotoPaths.length]);

  return (
    <div className="bg-slate-50/60 rounded border border-slate-100 px-2.5 py-2 flex items-start gap-2.5">
      {/* Alleen een echte afbeelding tonen; `fotosUrl` is een pagina en wordt
          een link. Zonder foto geen gebroken plaatje maar niets. */}
      {item.fotoUrl ? (
        item.fotosUrl ? (
          <a
            href={item.fotosUrl}
            target="_blank"
            rel="noreferrer"
            title="Bekijk alle foto's van dit kleed"
            className="flex-shrink-0"
          >
            <img
              src={item.fotoUrl}
              alt={item.wiNaam}
              className="w-10 h-10 rounded object-cover border border-slate-200 hover:border-indigo-400 transition-colors"
            />
          </a>
        ) : (
          <img
            src={item.fotoUrl}
            alt={item.wiNaam}
            className="w-10 h-10 rounded object-cover flex-shrink-0 border border-slate-200"
          />
        )
      ) : item.fotosUrl ? (
        <a
          href={item.fotosUrl}
          target="_blank"
          rel="noreferrer"
          className="w-10 h-10 rounded border border-dashed border-slate-300 flex-shrink-0 flex items-center justify-center text-[9px] text-slate-400 hover:text-indigo-600 hover:border-indigo-300 text-center leading-tight"
        >
          Foto's
        </a>
      ) : null}
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono font-medium text-slate-900">{item.wiNaam}</span>
          <span className={clsx(pil, FASE_PIL[fase] ?? 'border-slate-300 text-slate-600')}>
            {item.faseLabel ?? fase}
          </span>
          {item.opdrachtTekst && (
            <span className="text-[11px] text-slate-500 truncate">{item.opdrachtTekst}</span>
          )}
        </div>

        <ReparatieKleedTijdlijn item={item} />

        <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
          {kanHeen && !item.geplandeRitHeen && (
            <button
              type="button"
              onClick={() => onOpenPlan({ opdracht, mode: 'heen', wiId: item.wiId, wiLabel: item.wiNaam })}
              disabled={busy || !planToegestaan}
              title={planGeblokkeerdReden ?? undefined}
              className={clsx(knopSecundair, !planToegestaan && 'opacity-60')}
            >
              Plan heen-rit
            </button>
          )}
          {kanHeen && item.geplandeRitHeen && (
            <button
              type="button"
              onClick={() =>
                onOpenPlan({
                  opdracht,
                  mode: 'heen',
                  wiId: item.wiId,
                  wiLabel: item.wiNaam,
                  currentRitId: item.geplandeRitHeen?.id,
                })
              }
              disabled={busy}
              className={knopSecundair}
            >
              Wijzig heen-rit
            </button>
          )}
          {kanTerug && !item.geplandeRitTerug && (
            <button
              type="button"
              onClick={() => onOpenPlan({ opdracht, mode: 'terug', wiIds: [item.wiId], wiLabels: [item.wiNaam] })}
              disabled={busy}
              className={knopSecundair}
            >
              Plan terug-rit
            </button>
          )}
          {kanTerug && item.geplandeRitTerug && (
            <button
              type="button"
              onClick={() =>
                onOpenPlan({
                  opdracht,
                  mode: 'terug',
                  wiIds: [item.wiId],
                  wiLabels: [item.wiNaam],
                  currentRitId: item.geplandeRitTerug?.id,
                })
              }
              disabled={busy}
              className={knopSecundair}
            >
              Wijzig terug-rit
            </button>
          )}
          {correcties.length > 0 && (
            <CorrectieMenu
              opties={correcties}
              disabled={busy}
              onKies={(soort) => onOpenCorrectie(soort, item)}
            />
          )}
        </div>

        {eindfotoUrls.length > 0 && (
          <div className="pt-1">
            <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Eindfoto's reparateur</p>
            <div className="flex flex-wrap gap-1">
              {eindfotoUrls.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noreferrer">
                  <img src={url} alt={`Eindfoto ${i + 1}`} className="w-12 h-12 object-cover rounded border border-emerald-200" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================

interface CorrectieMenuProps {
  opties: Array<{ soort: CorrectieSoort; label: string }>;
  disabled: boolean;
  onKies: (soort: CorrectieSoort) => void;
}

/**
 * Het ⋯-menu: correcties op de fysieke stempels. Bewust onopvallend, want de
 * chauffeur (field-app) en de reparateur (portaal) zetten deze stempels in
 * het normale proces; kantoor grijpt alleen in.
 */
function CorrectieMenu({ opties, disabled, onKies }: CorrectieMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const sluit = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', sluit);
    return () => document.removeEventListener('mousedown', sluit);
  }, [open]);

  return (
    <div ref={ref} className="relative ml-auto">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className={clsx(knopTekst, 'border border-slate-200 bg-white')}
        title="Correcties op de stempels van dit kleed"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        ⋯ Corrigeren
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-md shadow-lg min-w-[15rem] py-1"
        >
          {opties.map((opt) => (
            <button
              key={opt.soort}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onKies(opt.soort);
              }}
              className={clsx(
                'w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50',
                opt.soort.startsWith('undo') ? 'text-amber-800' : 'text-slate-700'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================================

/**
 * Vult heen/terug-ritten aan die we zelf net geboekt hebben maar die nog niet
 * in de mirror staan. Alleen als terugval: zodra de mirror de rit kent wint
 * die, want dat is de echte bron.
 */
function applyVerseRitten(
  lijst: ReparatieOpdracht[],
  vers: Record<string, RitInfo>
): ReparatieOpdracht[] {
  if (Object.keys(vers).length === 0) return lijst;
  return lijst.map((o) => ({
    ...o,
    items: o.items.map((it) => {
      const heen = it.geplandeRitHeen ?? vers[`${it.wiId}:heen`] ?? null;
      const terug = it.geplandeRitTerug ?? vers[`${it.wiId}:terug`] ?? null;
      // De API berekende fase/label zonder deze verse rit; hier lokaal bijtrekken
      // zodat de kaart niet "Klaar bij reparateur" blijft zeggen na "Plan terug-rit".
      let fase = it.fase;
      let faseLabel = it.faseLabel;
      if (!it.geplandeRitHeen && heen && fase === 'in_wasserij') {
        fase = 'heen_gepland';
        faseLabel = `Gaat ${kortDatum(heen.datum)} naar ${o.reparateurNaam ?? 'de reparateur'}`;
      }
      if (!it.geplandeRitTerug && terug && fase === 'klaar') {
        fase = 'terug_gepland';
        faseLabel = `Wordt ${kortDatum(terug.datum)} opgehaald`;
      }
      return { ...it, geplandeRitHeen: heen, geplandeRitTerug: terug, fase, faseLabel };
    }),
  }));
}

function kortDatum(iso: string | null): string {
  if (!iso) return 'binnenkort';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
}

// =====================================================================

interface AnnulerenDialogProps {
  opdracht: ReparatieOpdracht;
  onClose: () => void;
  onCancelled: () => void;
}

function ReparatieAnnulerenDialog({ opdracht, onClose, onCancelled }: AnnulerenDialogProps) {
  const [reden, setReden] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCancel = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await cancelReparatie({
        opdrachtId: opdracht.id,
        reden: reden.trim() || undefined,
      });
      if (!res.success) {
        setError(res.message || 'Annuleren mislukt');
        setBusy(false);
        return;
      }
      onCancelled();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Annuleren mislukt');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Reparatie annuleren</h2>
          <p className="text-xs text-slate-500 mt-1">
            {opdracht.opdrachtNummer}
            {opdracht.reparateurNaam ? ` — ${opdracht.reparateurNaam}` : ''}
          </p>
        </div>
        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-slate-700">
            De opdracht wordt definitief geannuleerd. Het kleed blijft gewoon in de
            wasserij-flow en gaat mee met de normale terugbreng-route.
          </p>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Reden (optioneel)</label>
            <textarea
              rows={2}
              maxLength={255}
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
              value={reden}
              onChange={(e) => setReden(e.target.value)}
              disabled={busy}
              placeholder="Bijv. reparateur kan de reparatie niet uitvoeren"
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5">
            Terug
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={busy}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              busy ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : 'bg-rose-600 text-white hover:bg-rose-700'
            )}
          >
            {busy ? 'Annuleren…' : 'Definitief annuleren'}
          </button>
        </div>
      </div>
    </div>
  );
}
